import { createFileRoute, Link } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { DIRECTIONS, useDirection } from "@/lib/direction";
import { ArrowRight, Layers, Users, FileText, ShieldCheck, PenTool, Palette } from "lucide-react";

export const Route = createFileRoute("/pitch")({
  head: () => ({
    meta: [
      { title: "Medical Insight — Redesign Pitch Demo" },
      {
        name: "description",
        content:
          "An interactive pitch from your redesign team: four design directions for miinews.com, switchable live, with a working admin for the Board of Directors.",
      },
    ],
  }),
  component: PitchDemo,
});

function PitchDemo() {
  const { direction, setDirection } = useDirection();
  const current = DIRECTIONS.find((d) => d.id === direction);
  return (
    <div>
      <section className="relative overflow-hidden">
        <div className="brand-gradient absolute inset-0 opacity-[0.08]" aria-hidden />
        <div className="relative mx-auto max-w-7xl px-4 py-12 sm:py-20 lg:py-28">
          <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground sm:text-xs">
            Redesign pitch · prepared for Medical Insight, Inc.
          </p>
          <h1 className="mt-4 max-w-4xl text-[clamp(1.875rem,6vw,3.75rem)] font-bold leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl">
            Four directions for{" "}
            <span className="brand-gradient-text">miinews.com</span>, switchable live.
          </h1>
          <p className="mt-6 max-w-2xl text-base text-muted-foreground sm:text-lg">
            Click a direction card below (or append <code>?v=v2</code>…<code>?v=v5</code> to any
            URL) to preview a look; the live theme is managed in{" "}
            <span className="font-medium text-foreground">Admin &gt; Theme</span>. You're currently
            viewing <span className="font-semibold text-foreground">{current?.name}</span>.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="w-full sm:w-auto">
              <Link to="/welcome">
                See the home page <ArrowRight className="ml-1 h-4 w-4" />
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <Link to="/board">View the board (live data)</Link>
            </Button>
            {/* static pages outside the SPA router — plain anchors, not <Link> */}
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <a href="/logo-comps.html">
                <PenTool className="mr-1 h-4 w-4" /> Logo options
              </a>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto">
              <a href="/logo-comps-2.html">
                <Palette className="mr-1 h-4 w-4" /> Logo colourways
              </a>
            </Button>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-16">
        <h2 className="text-2xl font-semibold tracking-tight">What's in this demo</h2>
        <p className="mt-2 max-w-2xl text-muted-foreground">
          A clickable pitch — the public pages let you compare directions; the admin previews how
          the rearchitected backend would feel.
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <Feature
            icon={<Layers className="h-5 w-5" />}
            title="Five design directions"
            body="Five light-mode design systems, previewable here. The live one — colors, fonts and all — is set in Admin > Theme."
          />
          <Feature
            icon={<FileText className="h-5 w-5" />}
            title="Home in 4 looks"
            body="Hero, featured insights, research, and AIA blocks reskinned per direction."
          />
          <Feature
            icon={<Users className="h-5 w-5" />}
            title="Board of Directors (real CRUD)"
            body="Live database. Admin can add, edit, reorder, and remove board members."
          />
          <Feature
            icon={<ShieldCheck className="h-5 w-5" />}
            title="Role-based admin"
            body="Email/password + Google sign-in. Admin role enforced at the database with RLS."
          />
        </div>
      </section>

      <section className="mx-auto max-w-7xl px-4 pb-24">
        <h2 className="text-2xl font-semibold tracking-tight">The five directions</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
          {DIRECTIONS.map((d, i) => (
            <Card
              key={d.id}
              role="button"
              tabIndex={0}
              onClick={() => setDirection(d.id)}
              onKeyDown={(e) => e.key === "Enter" && setDirection(d.id)}
              className={
                "cursor-pointer transition-shadow hover:shadow-md " +
                (direction === d.id ? "ring-2 ring-ring" : "")
              }
            >
              <CardHeader>
                <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
                  Direction {i + 1}
                </div>
                <CardTitle className="mt-1 text-lg">{d.name}</CardTitle>
                <CardDescription>{d.tagline}</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="brand-gradient h-2 w-full rounded-full" />
                <p className="mt-3 text-xs text-muted-foreground">
                  {d.id === direction ? "Currently previewing" : "Click to preview"}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </div>
  );
}

function Feature({
  icon,
  title,
  body,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
}) {
  return (
    <Card>
      <CardHeader>
        <div className="flex h-9 w-9 items-center justify-center rounded-md bg-secondary text-secondary-foreground">
          {icon}
        </div>
        <CardTitle className="mt-3 text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="text-sm text-muted-foreground">{body}</CardContent>
    </Card>
  );
}
